import { useMutation, useQueryClient } from "@tanstack/react-query";

import { PostTaskRequest } from "@protos/ai/v1/tasks";
import { authPost, authRequestRaw, checkStatus } from "@shared/api";
import { TimeLookbackRange } from "@shared/types";
import { calculateLookbackSeconds } from "@shared/utils";
import { _getFindingsParamsFromSearch } from "@shared/utils";

interface PostAiTaskResponse {
  status: string;
  num_analyzed: number;
}

export const postAiFixedTask = async (
  request: PostTaskRequest,
  searchParams: URLSearchParams
): Promise<{}> => {
  return authRequestRaw(
    "post",
    `/api/ai/fixed_tasks?${searchParams.toString()}`,
    request
  ).then(checkStatus);
};

export const postAiFixedAdminTask = async (
  request: PostTaskRequest
): Promise<{}> => {
  return authRequestRaw("post", `/api/ai/admin_fixed_tasks`, request).then(
    checkStatus
  );
};

export const postAiTask = async (
  deploymentId: number,
  postTaskParams: AiTaskQueryParams | undefined,
  fixedTaskRequest: PostTaskRequest
): Promise<number> => {
  let queryParams = new URLSearchParams();
  const queryString = `/api/ai/${deploymentId}/combined_task`;
  if (postTaskParams) {
    if (postTaskParams.searchParams) {
      // Clean up search params
      queryParams = _getFindingsParamsFromSearch(postTaskParams.searchParams);
    }
    queryParams.set(
      "since",
      calculateLookbackSeconds(postTaskParams.timeLookbackRange).toString()
    );
    if (queryParams.get("tab") === null) {
      queryParams.set("tab", "open");
    }

    fixedTaskRequest.queryParams = queryParams.toString();
  }
  const response: PostAiTaskResponse = await authPost(
    queryString,
    fixedTaskRequest
  );
  return response.num_analyzed;
};

export type AiTaskQueryParams = {
  searchParams: URLSearchParams;
  timeLookbackRange: TimeLookbackRange;
};

export const usePostAiFixedAdminTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: PostTaskRequest) => {
      return postAiFixedAdminTask(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["aiTaskStatus"],
      });
    },
  });
};

export const usePostAiTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      deploymentId,
      queryTask,
      fixedTask,
    }: {
      deploymentId: number;
      queryTask: AiTaskQueryParams | undefined;
      fixedTask: PostTaskRequest;
    }) => {
      return postAiTask(deploymentId, queryTask, fixedTask);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["aiTaskStatus"],
      });
    },
  });
};
